<template>
  <div id="about">
    <v-container>
      <v-row class="mb-13 pb-13">
        <v-col
          cols="12"
          lg="4"
          :class="[$vuetify.breakpoint.mdAndDown ? 'text-center' : 'text-left', 'text-wrap']"
        >
          <h2 class="primary300--text mb-2">
            {{ banner.title }}
          </h2>
          <div v-html="banner.description"></div>
          <div v-if="banner.files.length > 0">
            <v-chip
              v-for="(file, index) in banner.files"
              :key="index"
              class="ma-2"
              color="primary200"
              text-color="white"
              @click="handleFileClick(file)"
            >
              {{ file }}
            </v-chip>
          </div>
          <v-btn
            v-show="applicationForm"
            v-if="role === 'researcher' || role === 'provider'"
            color="primary200"
            dark
            class="mt-10  pl-16 pr-16 pt-9 pb-9 white--text "
            x-large
            width="480px"
            @click="showApplyFormDialog"
          >
            Apply Now
          </v-btn>
          <v-btn
            v-show="applicationForm"
            v-else
            color="primary200"
            dark
            class="mt-10  pl-16 pr-16 pt-9 pb-9 white--text "
            x-large
            width="480px"
            :to="{ name: 'SignUp', params: { applyForm: true } }"
          >
            Apply Now
          </v-btn>
        </v-col>
        <v-spacer></v-spacer>
        <v-col
          cols="12"
          lg="6"
          class="text-right "
          :class="$vuetify.breakpoint.mdAndDown ? 'text-center' : 'text-right'"
        >
          <div class="image-wrapper">
            <v-img src="../../assets/img/landing-page/pages/new_about.jpg"></v-img>
            <div class="overlay"></div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Application Form Dialog -->
    <v-dialog v-model="applyFormTriggered" max-width="800" persistent>
      <v-card>
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="justify-start d-flex">
              Application Form
            </v-col>
          </v-row>
          <v-btn icon @click="closeApplyFormDialog" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="custom-divider"></v-divider>
        <v-card-text>
          <v-form ref="form">
            <v-row class="justify-center">
              <v-col cols="12" class="pb-0">
                <label class="font-weight-bold subtitle-2">{{ $t('firstName') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.firstName"
                  :placeholder="'Fill your first name'"
                  outlined
                  required
                  :rules="requiredRule()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('lastName') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.lastName"
                  :placeholder="'Fill your last name'"
                  outlined
                  required
                  :rules="requiredRule()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('email') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.email"
                  :placeholder="'Fill your email'"
                  outlined
                  required
                  :rules="emailRules()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('institution') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.institution"
                  :placeholder="'Fill your institution'"
                  outlined
                  required
                  :rules="requiredRule()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('position') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.position"
                  :placeholder="'Fill your position'"
                  outlined
                  required
                  :rules="requiredRule()"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('mobilePhone') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.mobilePhone"
                  :placeholder="'Fill your phone'"
                  outlined
                  required
                  :rules="requiredRule()"
                  type="number"
                  hide-spin-buttons
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" class="pt-0">
                <label class="text-bold subtitle-2">{{ $t('description') }}</label>
                <tiptap-vuetify
                  v-model="contactForm.description"
                  :placeholder="$t('description')"
                  :extensions="extensions"
                  @blur="validateDescription"
                  @input="validateDescription"
                ></tiptap-vuetify>
                <div class="text-caption mb-8 v-messages" v-text="counterMessage"></div>
                <v-alert v-if="descriptionError" type="error" class="subtitle-2 py-2">
                  {{ descriptionError }}
                </v-alert>
              </v-col>
              <v-col cols="12 pt-0">
                <label class="text-bold subtitle-2">{{ $t('Link') }}</label>
                <v-text-field
                  class="pt-2"
                  v-model="contactForm.link"
                  :placeholder="'Enter link'"
                  outlined
                  @input="validateUrl"
                >
                </v-text-field>
                <v-alert v-if="!isValidUrl" type="error" class="subtitle-2 py-2">
                  Please enter a valid URL
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-row>
            <v-col>
              <v-btn
                large
                class="rounded-lg black--text  font-weight-bold"
                outlined
                color="black"
                block
                @click="nextDialog"
                >{{ $t('next') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Question Dialog -->
    <v-dialog v-model="questionDialog" max-width="800">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-form ref="question">
          <v-card-text class="mt-0" v-if="firstQuestion"
            ><h2>
              {{ firstQuestion }}
            </h2>
          </v-card-text>
          <v-card-text class="mt-0" v-if="firstQuestion">
            <v-radio-group v-model="selectedAnswer" required :rules="requiredRule()">
              <v-radio
                v-for="answer in campaignAnswers"
                :key="answer.id"
                :label="answer.answer"
                :value="answer"
              ></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-text class="mt-0" v-if="secondQuestion">
            <h2>
              {{ secondQuestion }}
            </h2>
          </v-card-text>
          <v-card-text v-if="secondQuestion">
            <v-textarea
              v-model="freeTextAnswer"
              label="Message"
              maxlength="100"
              counter
              outlined
              single-line
              required
              :rules="requiredRule()"
            ></v-textarea>
          </v-card-text>
        </v-form>
        <v-card-actions class="pt-0">
          <v-row>
            <v-col cols="6" class="justify-start d-flex">
              <v-btn
                large
                class="rounded-lg black--text  font-weight-bold"
                outlined
                color="black"
                block
                @click="closeQuestionDialog"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6" class="justify-end d-flex">
              <v-btn
                large
                class="rounded-lg white--text  font-weight-bold"
                color="neutral"
                block
                @click="onSubmitApplicationForm"
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import util from '@/mixins/util';
import { Bold, Underline, Italic, Strike, TiptapVuetify } from 'tiptap-vuetify';
import EventBus from '@/helpers/event-bus.js';
import service from '@/services/base-service';

export default {
  name: 'Banner',
  mixins: [util, service],
  components: { TiptapVuetify },
  data: () => ({
    applyFormTriggered: false,
    descriptionError: null,
    counter: 500,
    banner: {
      title: '',
      description: '',
      files: [],
    },
    files: [],
    id: null,
    firstQuestion: '',
    firstQuestionId: null,
    secondQuestionId: null,
    campaignAnswers: [],
    secondQuestion: '',
    selectedAnswer: null,
    freeTextAnswer: '',
    contactForm: {
      firstName: '',
      lastName: '',
      mobilePhone: null,
      email: '',
      institution: '',
      description: '',
      position: '',
      link: '',
    },
    applicationForm: false,
    questionDialog: false,
    isValidUrl: true,
    extensions: [Bold, Underline, Italic, Strike],
  }),

  mounted() {
    this.getDataFromApi()
      .then(responseData => {
        console.log('Data received from API:', responseData);
        this.banner.title = responseData.title;
        this.banner.description = responseData.description
          ? responseData.description
          : 'Description Text';
        this.banner.files = responseData.files;
        this.applicationForm = responseData.applicationForm;
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      });

    this.contactForm.email = localStorage.getItem('email') || '';
    this.contactForm.position = localStorage.getItem('roles' || '');
    const fullName = localStorage.getItem('userfullname') || '';

    if (fullName) {
      const nameParts = fullName.split(' ');

      this.contactForm.lastName = nameParts.pop() || '';

      this.contactForm.firstName = nameParts.join(' ') || '';
    }
  },

  methods: {
    handleFileClick(file) {
      const encodedFile = encodeURIComponent(file);
      const filePath = `api/v1/campaign/${this.id}/file/${encodedFile}`;

      this.axios
        .get(filePath, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'],
          });

          const fileURL = URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = fileURL;
          a.download = file;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          URL.revokeObjectURL(fileURL);
        })
        .catch(error => {
          console.error('Error fetching file:', error);
        });
    },
    showApplyFormDialog() {
      this.applyFormTriggered = true;
    },
    closeQuestionDialog() {
      this.contactForm.name = null;
      this.contactForm.email = null;
      this.contactForm.institution = null;
      this.contactForm.description = null;
      this.descriptionError = null;
      this.selectedAnswer = '';
      this.freeTextAnswer = '';
      this.$refs.form.reset();
      this.$refs.question.reset();
      this.questionDialog = false;
      this.applyFormTriggered = false;
    },
    closeApplyFormDialog() {
      this.contactForm.name = null;
      this.contactForm.email = null;
      this.contactForm.institution = null;
      this.contactForm.description = null;
      this.descriptionError = null;
      this.selectedAnswer = '';
      this.freeTextAnswer = '';
      this.$refs.form.reset();
      this.questionDialog = false;
      this.applyFormTriggered = false;
      this.isValidUrl = true;
    },
    minMaxLengthRule(min, max) {
      return [
        v => !!v || this.$i18n.t('required-field'),
        v => (v && v.length >= min) || 'This field must have at least ' + '10' + ' characters',
        v =>
          (v && v.length <= max) || 'This field exceeds ' + '500' + ' maximum allowed characters',
      ];
    },
    validateDescription() {
      const rules = this.minMaxLengthRule(17, 507);
      for (let rule of rules) {
        const error = rule(this.contactForm.description);
        if (error !== true) {
          this.descriptionError = error;
          return false;
        }
      }
      this.descriptionError = null;
      return true;
    },
    validateUrl() {
      this.isValidUrl = this.canParse(this.contactForm.link);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
    getDataFromApi() {
      console.log('Loading data');

      return new Promise((resolve, reject) => {
        this.axios
          .get('api/v1/campaign/published')
          .then(res => {
            console.log('API response:', res);
            if (res.status === 200) {
              const responseData = res.data;
              console.log('Extracted data:', responseData);
              this.id = responseData.id;
              resolve(responseData);
            } else {
              reject(new Error(`API request failed with status: ${res.status}`));
            }
          })
          .catch(error => {
            console.error('Error fetching data:', error);

            reject(error);
          });
      });
    },
    getCampaignQuestions() {
      if (!this.id) {
        console.error('ID is not available');
        return Promise.reject('ID is not available');
      }

      console.log('Loading questions');

      return new Promise((resolve, reject) => {
        this.axios
          .get(`api/v1/campaign/${this.id}/questions`)
          .then(res => {
            console.log('API response:', res);
            if (res.status === 200) {
              const responseData = res.data;
              console.log('Extracted data:', responseData);
              resolve(responseData);
            } else {
              reject(new Error(`API request failed with status: ${res.status}`));
            }
          })
          .catch(error => {
            console.error('Error fetching data:', error);

            reject(error);
          });
      });
    },
    nextDialog() {
      const isFormValid = this.$refs.form.validate();

      if (isFormValid) {
        this.questionDialog = true;

        this.getCampaignQuestions()
          .then(responseData => {
            console.log('Questions received from API:', responseData);

            const questionWithAnswers = responseData.find(item => item.hasAnswers === true);

            const questionWithoutAnswers = responseData.find(item => item.hasAnswers === false);

            if (questionWithAnswers) {
              this.firstQuestion = questionWithAnswers.question;
              this.campaignAnswers = questionWithAnswers.campaignAnswers;
              this.firstQuestionId = questionWithAnswers.id;
            }

            if (questionWithoutAnswers) {
              this.secondQuestion = questionWithoutAnswers.question;
              this.secondQuestionId = questionWithoutAnswers.id;
            }
          })
          .catch(error => {
            console.error('Error fetching questions:', error);
          });
      } else {
        console.error('Please fill all fields in the contact form.');
      }
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },

    onSubmitApplicationForm() {
      const isFormValid = this.$refs.question.validate();
      if (isFormValid) {
        const campaignApplicantQuestions = [];

        if (this.firstQuestionId) {
          campaignApplicantQuestions.push({
            campaignQuestion: {
              question: this.firstQuestion,
              id: this.firstQuestionId,
            },
            campaignAnswer: {
              answer: this.selectedAnswer?.answer,
              id: this.selectedAnswer?.id,
            },
          });
        }

        if (this.secondQuestionId) {
          campaignApplicantQuestions.push({
            campaignQuestion: {
              question: this.secondQuestion,
              id: this.secondQuestionId,
            },
            freeTextAnswer: this.freeTextAnswer,
          });
        }

        if (this.id) {
          const payload = {
            campaignId: this.id,
            firstname: this.contactForm.firstName,
            lastname: this.contactForm.lastName,
            email: this.contactForm.email,
            institution: this.contactForm.institution,
            position: this.contactForm.position,
            mobilePhone: this.contactForm.mobilePhone,
            description: this.contactForm.description,
            link: this.contactForm.link,
            campaignApplicantQuestions,
          };

          this.deleteEmptyProperties(payload);

          this.axios
            .put(`/api/v1/campaign`, payload)
            .then(response => {
              console.log('Successfully updated:', response.data);

              EventBus.$emit('snackbar', {
                text: 'Success Apply.',
              });
              this.closeApplyFormDialog();
              this.closeQuestionDialog();
            })
            .catch(error => {
              console.error('Error updating campaign:', error);
              EventBus.$emit('snackbar', {
                text: error,
              });
            });
        }
      }
    },
  },
  computed: {
    role() {
      return this.$store.getters.roles;
    },
    counterMessage() {
      let descriptionLength = 0;

      if (this.contactForm.description) {
        descriptionLength = this.contactForm.description.length;
      }

      if (this.counter === -1) {
        return '';
      } else if (!this.contactForm.description) {
        return '0' + ' / ' + this.counter;
      } else {
        return descriptionLength - 7 + ' / ' + this.counter;
      }
    },
  },
};
</script>

<style scoped>
.image-wrapper {
  position: relative;
  display: inline-block;
}

.image-wrapper .v-image {
  display: block;
  width: 700px;
  height: auto;
  border-radius: 24px;
}

#about .v-btn {
  font-weight: bold;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .image-wrapper {
    max-width: 100%;
    overflow: hidden;
  }

  .image-wrapper .v-image {
    width: 100%;
    height: auto;
  }
}

@media (max-width: 425px) {
  #about .v-btn {
    font-size: 14px;
    max-width: 250px;
  }
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(100, 0, 255, 0.2);
  z-index: 0;
  pointer-events: none;
  border-radius: 24px;
}

.custom-divider {
  padding: 5px 0;
  margin: 0 35px;
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: rgba(195, 194, 199, 1);
}

.text-wrap {
  word-wrap: break-word;
  overflow-wrap: break-word;
  padding: 0 16px;
}
</style>
